<script setup lang="ts">
import { computed } from "vue"
import { XMarkIcon } from "@heroicons/vue/24/outline"
import { useOverlay } from "@/composables/useOverlay"

export type DrawerPosition = "left" | "right" | "top" | "bottom"
export type DrawerSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full"

const props = withDefaults(
  defineProps<{
    title?: string
    position?: DrawerPosition
    size?: DrawerSize
    closeOnClickOutside?: boolean
    closeOnEscape?: boolean
    showCloseButton?: boolean
    showOverlay?: boolean
    drawerClass?: string
    overlayClass?: string
    persistent?: boolean
    zIndex?: number
    fullHeight?: boolean
  }>(),
  {
    position: "right",
    size: "md",
    closeOnClickOutside: true,
    closeOnEscape: true,
    showCloseButton: true,
    showOverlay: true,
    persistent: false,
    zIndex: 50,
    fullHeight: true,
  }
)

const emit = defineEmits<{
  close: []
  "before-open": []
  opened: []
  "before-close": []
  closed: []
}>()

const modelValue = defineModel<boolean>({ required: true })

const { overlayRef, contentRef, handleClickOutside, close } = useOverlay(modelValue, {
  closeOnEscape: props.persistent ? false : props.closeOnEscape,
  closeOnClickOutside: props.persistent ? false : props.closeOnClickOutside,
  onClose: () => {
    if (!props.persistent) {
      modelValue.value = false
      emit("close")
    }
  },
})

const sizeClasses = computed(() => {
  const isHorizontal = props.position === "left" || props.position === "right"

  if (isHorizontal) {
    const widths: Record<DrawerSize, string> = {
      sm: "w-64",
      md: "w-80",
      lg: "w-96",
      xl: "w-[32rem]",
      "2xl": "w-[48rem]",
      "3xl": "w-[64rem]",
      full: "w-full",
    }
    return widths[props.size ?? "md"]
  }

  const heights: Record<DrawerSize, string> = {
    sm: "h-32",
    md: "h-48",
    lg: "h-64",
    xl: "h-96",
    "2xl": "h-[48rem]",
    "3xl": "h-[64rem]",
    full: "h-full",
  }
  return heights[props.size ?? "md"]
})

const positionClasses = computed(() => {
  const positions: Record<DrawerPosition, string> = {
    left: "left-0 top-0 bottom-0",
    right: "right-0 top-0 bottom-0",
    top: "top-0 left-0 right-0",
    bottom: "bottom-0 left-0 right-0",
  }
  return positions[props.position ?? "right"]
})

const transitionName = computed(() => `drawer-${props.position ?? "right"}`)

const zIndexClass = computed(() => {
  const zIndexMap: Record<number, string> = {
    10: "z-10",
    20: "z-20",
    30: "z-30",
    40: "z-40",
    50: "z-50",
    60: "z-60",
  }
  return zIndexMap[props.zIndex!] || "z-50"
})

const handleTransitionBeforeEnter = () => emit("before-open")
const handleTransitionAfterEnter = () => emit("opened")
const handleTransitionBeforeLeave = () => emit("before-close")
const handleTransitionAfterLeave = () => emit("closed")

const handleClose = () => {
  if (!props.persistent) close()
}
</script>

<template>
  <Teleport to="body">
    <Transition
      name="drawer-overlay"
      @before-enter="handleTransitionBeforeEnter"
      @after-enter="handleTransitionAfterEnter"
      @before-leave="handleTransitionBeforeLeave"
      @after-leave="handleTransitionAfterLeave"
    >
      <div
        v-if="modelValue"
        ref="overlayRef"
        :class="['fixed inset-0 drawer-container', zIndexClass, overlayClass]"
      >
        <Transition name="drawer-backdrop">
          <div
            v-if="showOverlay && modelValue"
            class="absolute inset-0 bg-black/50"
            aria-hidden="true"
            @click="handleClickOutside"
          />
        </Transition>

        <Transition :name="transitionName">
          <div
            v-if="modelValue"
            ref="contentRef"
            :class="[
              'absolute bg-white shadow-2xl flex flex-col',
              positionClasses,
              sizeClasses,
              drawerClass,
            ]"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="title ? 'drawer-title' : undefined"
          >
            <div
              v-if="title || $slots.header || showCloseButton"
              class="flex items-center justify-between p-4 border-b border-slate-200 shrink-0"
            >
              <div class="flex-1">
                <slot name="header">
                  <h2 v-if="title" id="drawer-title" class="text-lg font-semibold text-slate-900">
                    {{ title }}
                  </h2>
                </slot>
              </div>

              <button
                v-if="showCloseButton"
                type="button"
                class="ml-auto -m-2 p-2 text-slate-400 hover:text-slate-500 rounded-lg transition-colors"
                aria-label="Fechar"
                @click="handleClose"
              >
                <XMarkIcon class="w-5 h-5" />
              </button>
            </div>

            <div class="flex-1 overflow-y-auto p-4">
              <slot />
            </div>

            <div
              v-if="$slots.footer"
              class="flex items-center justify-end gap-3 p-4 border-t border-slate-200 shrink-0"
            >
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style>
.drawer-container {
  background: transparent !important;
}

.drawer-overlay-enter-active,
.drawer-overlay-leave-active {
  transition: opacity 0.3s ease;
}
.drawer-overlay-enter-from,
.drawer-overlay-leave-to {
  opacity: 0;
}

.drawer-backdrop-enter-active,
.drawer-backdrop-leave-active {
  transition: opacity 0.3s ease;
}
.drawer-backdrop-enter-from,
.drawer-backdrop-leave-to {
  opacity: 0;
}

.drawer-left-enter-active,
.drawer-left-leave-active {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.drawer-left-enter-from,
.drawer-left-leave-to {
  transform: translateX(-100%);
}

.drawer-right-enter-active,
.drawer-right-leave-active {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.drawer-right-enter-from,
.drawer-right-leave-to {
  transform: translateX(100%);
}

.drawer-top-enter-active,
.drawer-top-leave-active {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.drawer-top-enter-from,
.drawer-top-leave-to {
  transform: translateY(-100%);
}

.drawer-bottom-enter-active,
.drawer-bottom-leave-active {
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}
.drawer-bottom-enter-from,
.drawer-bottom-leave-to {
  transform: translateY(100%);
}
</style>
